import {useQueryClient} from '@github-ui/react-query'
import {FetchCAPIError} from '@github-ui/copilot-shared-ui/capi/fetch-capi'
import {useCAPIQuery} from '@github-ui/copilot-shared-ui/capi/use-capi-query'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {connectConnector, disconnectConnector, fetchPlugins} from '../data/connectors-api'
import {isConnected, normalizeConnectionStatus, type Plugin, pluginDisplayName} from '../types/connectors'
import {connectorErrorMetadata, startConnectorAttempt, type ConnectorActionSource} from '../utils/connector-analytics'
import {navigateToConsent, openBlankTab} from '../utils/consent-window'

export const connectorsQueryKey = ['copilot-connectors', 'plugins']

/** Gap between catalog reads while consent is open in the other tab. */
const CONSENT_POLL_INTERVAL_MS = 2_000

/** How long consent is watched before the user is asked to check back. */
const CONSENT_TIMEOUT_MS = 5 * 60 * 1000

const CONFIRMATION_TIMEOUT_MS = 10_000

const NO_PLUGINS: Plugin[] = []

const NO_BUSY_CONNECTORS: ReadonlySet<string> = new Set()

function hasConnected(plugins: Plugin[] | undefined, name: string): boolean {
  return plugins?.some(plugin => plugin.name === name && isConnected(plugin)) ?? false
}

/** True while a connect is still worth watching: before its deadline, tab still open, not yet connected. */
function pendingIsOpen(entry: PendingConsent, plugins: Plugin[] | undefined, now: number): boolean {
  if (entry.expiresAt <= now) return false
  // Pop-up blockers can open the tab without handing back a handle. With no
  // handle the deadline is the only stopping condition.
  if (entry.tab?.closed) return false
  return !hasConnected(plugins, entry.connector)
}

/** True while any connect is still worth watching. */
function consentIsOpen(pending: readonly PendingConsent[], plugins: Plugin[] | undefined, now: number): boolean {
  return pending.some(entry => pendingIsOpen(entry, plugins, now))
}

/**
 * Consent is tracked per connector. A row-level Connect leaves the other rows
 * enabled, so a second consent tab can open while the first is still out. One
 * shared slot would let the second overwrite the first connector's spinner,
 * tab watcher, and deadline.
 */
interface PendingConsent {
  connector: string
  attempt: ReturnType<typeof startConnectorAttempt>
  expiresAt: number
  /** Handle to the consent tab, so abandoning it can stop the polling. */
  tab: Window | null
}

const NO_PENDING: readonly PendingConsent[] = []

/**
 * Busy state is tracked per connector because the dialog can be closed
 * mid-request, which lets a second connector's action start while the first is
 * still running. Each action clears only the name it added.
 *
 * These are module scope so they stay out of the callback dependency arrays.
 * The compiler cannot lower a try/finally, so a hook-scoped helper called only
 * from a finally block reads as an unused dependency.
 */
function withBusy(name: string) {
  return (current: ReadonlySet<string>): ReadonlySet<string> => new Set(current).add(name)
}

function withoutBusy(name: string) {
  return (current: ReadonlySet<string>): ReadonlySet<string> => {
    if (!current.has(name)) return current
    const next = new Set(current)
    next.delete(name)
    return next.size === 0 ? NO_BUSY_CONNECTORS : next
  }
}

/**
 * Catalog state plus the connect and disconnect actions.
 *
 * Consent happens on the provider's own page and the service never reports
 * when it finishes, so the catalog is re-read on a timer while the consent tab
 * is open and again when this tab regains focus.
 */
export function useConnectors() {
  const queryClient = useQueryClient()
  const [pending, setPending] = useState<readonly PendingConsent[]>(NO_PENDING)
  const [busyConnectors, setBusyConnectors] = useState<ReadonlySet<string>>(NO_BUSY_CONNECTORS)
  const [actionError, setActionError] = useState<string | null>(null)
  const settlingAttemptsRef = useRef(new Set<string>())
  const confirmationQueueRef = useRef<Promise<void>>(Promise.resolve())

  const query = useCAPIQuery<Plugin[]>({
    queryKey: connectorsQueryKey,
    queryFn: ({signal}) => fetchPlugins(signal),
    refetchInterval: ({state}) => (consentIsOpen(pending, state.data, Date.now()) ? CONSENT_POLL_INTERVAL_MS : false),
    refetchOnWindowFocus: true,
  })

  const plugins = query.data ?? NO_PLUGINS

  // Derived, so landing on consent stops the polling without a second render
  // pass. The deadline is left to the timer below, since render stays off the
  // clock.
  const awaitingConnectors = useMemo(() => {
    if (pending.length === 0) return NO_BUSY_CONNECTORS
    const names = pending.filter(entry => !hasConnected(query.data, entry.connector)).map(entry => entry.connector)
    return names.length === 0 ? NO_BUSY_CONNECTORS : new Set(names)
  }, [pending, query.data])

  const refresh = useCallback(() => queryClient.invalidateQueries({queryKey: connectorsQueryKey}), [queryClient])

  const confirmConnection = useCallback(
    async (
      connector: string,
      attempt: ReturnType<typeof startConnectorAttempt>,
      reason: 'connection_unconfirmed' | 'tab_closed' | 'timeout',
    ) => {
      const timeoutError = new Error('Connector confirmation timed out')
      let expired = false
      let started = false
      let timer: ReturnType<typeof setTimeout> | undefined
      const previous = confirmationQueueRef.current
      const {promise, resolve: release} = Promise.withResolvers<void>()
      confirmationQueueRef.current = promise
      // Serialize final checks so cancelling an older poll cannot cancel another
      // attempt's confirmation. Each queued check starts after its own trigger.
      const read = (async () => {
        await previous
        if (expired) throw timeoutError
        started = true
        await queryClient.cancelQueries({queryKey: connectorsQueryKey, exact: true})
        if (expired) throw timeoutError
        return queryClient.fetchQuery({
          queryKey: connectorsQueryKey,
          queryFn: ({signal}) => fetchPlugins(signal),
          staleTime: 0,
        })
      })()
      const confirmation = Promise.race([
        read,
        new Promise<never>((_, reject) => {
          // Include time spent waiting behind another confirmation in the bound.
          timer = setTimeout(() => {
            expired = true
            reject(timeoutError)
            if (started) void queryClient.cancelQueries({queryKey: connectorsQueryKey, exact: true})
          }, CONFIRMATION_TIMEOUT_MS)
        }),
      ])
      try {
        const catalog = await confirmation
        const plugin = catalog.find(entry => entry.name === connector)
        const connected = plugin !== undefined && isConnected(plugin)
        attempt.finish({
          outcome: connected ? 'success' : 'unknown',
          reason: connected ? 'connection_confirmed' : reason,
          connection_status: normalizeConnectionStatus(plugin?.connection?.status),
        })
        return connected
      } catch (error) {
        if (error === timeoutError) {
          attempt.finish({outcome: 'unknown', reason: 'catalog_refresh_timeout'})
          setActionError('Could not confirm the connection. Try again.')
        } else {
          attempt.finish({outcome: 'unknown', reason: 'catalog_refresh_failed', ...connectorErrorMetadata(error)})
        }
        return false
      } finally {
        clearTimeout(timer)
        release()
      }
    },
    [queryClient],
  )

  // Once a connector reports connected its entry has nothing left to watch. It
  // drops out of `awaitingConnectors`, which re-runs the effect below and clears
  // its timers rather than letting the deadline raise a late error.
  useEffect(() => {
    if (pending.length === 0) return

    const timers: Array<ReturnType<typeof setTimeout>> = []
    const watchers: Array<ReturnType<typeof setInterval>> = []
    const drop = (entry: PendingConsent) =>
      setPending(current => (current.includes(entry) ? current.filter(item => item !== entry) : current))
    const settle = async (entry: PendingConsent, reason: 'tab_closed' | 'timeout') => {
      if (settlingAttemptsRef.current.has(entry.attempt.id)) return
      settlingAttemptsRef.current.add(entry.attempt.id)
      const connected = await confirmConnection(entry.connector, entry.attempt, reason)
      if (!connected && reason === 'timeout') {
        setActionError('Still waiting on authorization. Reload the page to check again.')
      }
      drop(entry)
      settlingAttemptsRef.current.delete(entry.attempt.id)
    }

    for (const entry of pending) {
      if (settlingAttemptsRef.current.has(entry.attempt.id)) continue
      if (!awaitingConnectors.has(entry.connector)) {
        entry.attempt.finish({
          outcome: 'success',
          reason: 'connection_confirmed',
          connection_status: 'connected',
        })
        drop(entry)
        continue
      }

      const remainingMs = entry.expiresAt - Date.now()
      const timer = setTimeout(
        () => {
          void settle(entry, 'timeout')
        },
        Math.max(0, remainingMs),
      )
      timers.push(timer)

      const tab = entry.tab
      if (!tab) continue

      // Consent may have succeeded just before the tab closed.
      const watcher = setInterval(() => {
        if (!tab.closed) return
        void settle(entry, 'tab_closed')
      }, CONSENT_POLL_INTERVAL_MS)
      watchers.push(watcher)
    }

    return () => {
      for (const timer of timers) clearTimeout(timer)
      for (const watcher of watchers) clearInterval(watcher)
    }
  }, [pending, awaitingConnectors, confirmConnection])

  const connect = useCallback(
    async (plugin: Plugin, source: ConnectorActionSource) => {
      const attempt = startConnectorAttempt('connect', plugin.name, source)
      setActionError(null)
      setBusyConnectors(withBusy(plugin.name))

      // Opened before the request so the click still counts as the gesture
      // that authorized it.
      const tab = openBlankTab()

      try {
        const connection = await connectConnector(plugin.name)

        if (!connection.consent_link) {
          tab?.close()
          await confirmConnection(plugin.name, attempt, 'connection_unconfirmed')
          return
        }

        if (!navigateToConsent(tab, connection.consent_link)) {
          attempt.finish({outcome: 'failure', reason: 'consent_navigation_failed'})
          setActionError(
            `Could not open the authorization page for ${pluginDisplayName(plugin)}. Allow pop-ups for this site and try again.`,
          )
          return
        }

        setPending(current => [
          ...current.filter(entry => entry.connector !== plugin.name),
          {connector: plugin.name, attempt, expiresAt: Date.now() + CONSENT_TIMEOUT_MS, tab},
        ])
      } catch (error) {
        attempt.finish({outcome: 'failure', reason: 'request_failed', ...connectorErrorMetadata(error)})
        tab?.close()
        setActionError(messageFor(error, `Could not connect ${pluginDisplayName(plugin)}.`))
      } finally {
        setBusyConnectors(withoutBusy(plugin.name))
      }
    },
    [confirmConnection],
  )

  const disconnect = useCallback(
    async (plugin: Plugin, source: ConnectorActionSource) => {
      const attempt = startConnectorAttempt('disconnect', plugin.name, source)
      setActionError(null)
      setBusyConnectors(withBusy(plugin.name))
      setPending(current =>
        current.some(entry => entry.connector === plugin.name)
          ? current.filter(entry => entry.connector !== plugin.name)
          : current,
      )

      try {
        await disconnectConnector(plugin.name)
        attempt.finish({outcome: 'success', reason: 'disconnection_confirmed'})
        await refresh()
      } catch (error) {
        attempt.finish({outcome: 'failure', reason: 'request_failed', ...connectorErrorMetadata(error)})
        setActionError(messageFor(error, `Could not disconnect ${pluginDisplayName(plugin)}.`))
      } finally {
        setBusyConnectors(withoutBusy(plugin.name))
      }
    },
    [refresh],
  )

  // The button keeps its loading state while consent is open in the other tab.
  const busy = useMemo(() => {
    if (awaitingConnectors.size === 0) return busyConnectors
    const next = new Set(busyConnectors)
    for (const name of awaitingConnectors) next.add(name)
    return next
  }, [awaitingConnectors, busyConnectors])

  return {
    plugins,
    isLoading: query.isLoading,
    loadError: query.isError ? messageFor(query.error, 'Could not load connectors.') : null,
    actionError,
    busyConnectors: busy,
    connect,
    disconnect,
    refresh,
  }
}

/** Server copy is already written for users. Anything else gets the fallback. */
function messageFor(error: unknown, fallback: string): string {
  return error instanceof FetchCAPIError ? error.message : fallback
}
